"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { isNavMatch } from "@/components/shell/nav-active";
import { ChevronDown } from "lucide-react";
import type { ProductKey } from "@/lib/billing/entitlements";
import { signedOutTrialHref } from "@/lib/landing-constants";
import { trackMarketingCtaClick } from "@/utils/analytics";

export interface MenuItem {
  id: number;
  title: string;
  url: string;
  appUrl?: string;
  appProduct?: ProductKey;
  dropdown: boolean;
  items?: MenuItem[];
  onClick?: () => void;
  external?: boolean;
  badge?: string;
  section?: "Free" | "Pro";
}

interface NavBarProps {
  list: MenuItem[];
  isLoading?: boolean;
  isAuthenticated?: boolean;
  sticky?: boolean;
  className?: string;
}

function HashAwareLink({
  href,
  className,
  children,
  onClick,
  role,
  planChooser,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  role?: string;
  planChooser?: string;
}) {
  if (href.startsWith("#")) {
    return (
      <a href={href} className={className} onClick={onClick} role={role} data-plan-chooser={planChooser}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={className} onClick={onClick} role={role}>
      {children}
    </Link>
  );
}

const NavBar: React.FC<NavBarProps> = ({
  list,
  isLoading = false,
  isAuthenticated = false,
  sticky = true,
  className = "",
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();
  const dropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const mobileDropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown !== null) {
        const desktopNode = dropdownRefs.current[activeDropdown];
        const mobileNode = mobileDropdownRefs.current[activeDropdown];

        if (desktopNode && desktopNode.contains(event.target as Node)) {
          return;
        }

        if (mobileNode && mobileNode.contains(event.target as Node)) {
          return;
        }

        setActiveDropdown(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeDropdown]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const { style } = document.body;
    const previousOverflow = style.overflow;
    style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    const mdViewport = window.matchMedia("(min-width: 768px)");
    const handleViewportChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    mdViewport.addEventListener("change", handleViewportChange);

    return () => {
      style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      mdViewport.removeEventListener("change", handleViewportChange);
    };
  }, [isMobileMenuOpen]);

  const toggleDropdown = (id: number) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const isAuthActionItem = (item: MenuItem) =>
    item.title === "Sign In" || item.title === "Sign Up" || item.title === "Start free trial";

  const visibleItems = list.filter((item) => !(isAuthActionItem(item) && isLoading));
  const mobileNavItems = visibleItems.filter((item) => !isAuthActionItem(item));
  const mobileAuthItems = visibleItems.filter(isAuthActionItem);

  const sectionLabel = (item: MenuItem, previous: MenuItem | undefined) => {
    if (!item.section || previous?.section === item.section) return null;
    return (
      <p
        className={`px-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground ${
          previous ? "mt-1 border-t border-border pt-3" : "pt-2"
        }`}
      >
        {item.section}
      </p>
    );
  };

  const renderTitle = (item: MenuItem) => (
    <span className="inline-flex items-center gap-2">
      <span>{item.title}</span>
      {item.badge && (
        <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-success/40 text-success bg-success/15">
          {item.badge}
        </span>
      )}
    </span>
  );

  const navClassName = `${sticky ? "sticky top-0" : "relative"} z-50 w-full border-b backdrop-blur-xl backdrop-saturate-150 transition-colors duration-300 ${
    isMobileMenuOpen
      ? "bg-background border-border"
      : isScrolled
        ? "bg-background/80 border-border"
        : "bg-background border-transparent"
  } ${className}`;

  return (
    <nav className={navClassName}>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link href="/" className="flex items-center flex-shrink-0">
            <Image
              src="/images/sportsbooks/upside_logo.png"
              alt="Upside Logo"
              width={96}
              height={32}
              sizes="96px"
              className="h-8 w-auto"
            />
          </Link>

          <div className="flex items-center gap-2 md:hidden">
            {!isAuthenticated && !isLoading && (
              <HashAwareLink
                href={signedOutTrialHref(pathname)}
                planChooser="navbar_mobile"
                onClick={() => {
                  closeMobileMenu();
                  trackMarketingCtaClick({
                    surface: "navbar_mobile",
                    cta: "start_trial",
                    destination: signedOutTrialHref(pathname),
                  });
                }}
                className="inline-flex h-9 items-center whitespace-nowrap rounded-lg bg-accent-value px-3 text-sm font-semibold text-zinc-950 hover:bg-accent-value-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                Start free trial
              </HashAwareLink>
            )}
            <button
              type="button"
              className="inline-flex w-11 h-11 min-w-[44px] min-h-[44px] items-center justify-center rounded-md text-foreground hover:text-primary hover:bg-background/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? "Close main menu" : "Open main menu"}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span className="sr-only">{isMobileMenuOpen ? "Close main menu" : "Open main menu"}</span>
              <svg
                className={`${isMobileMenuOpen ? "hidden" : "block"} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg
                className={`${isMobileMenuOpen ? "block" : "hidden"} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="hidden md:flex md:items-center md:space-x-6">
              {visibleItems
                .map((item) => (
                  <div
                    key={item.id}
                    className="relative"
                    ref={(el) => {
                      dropdownRefs.current[item.id] = el;
                    }}
                  >
                    {item.onClick ? (
                      <button type="button"
                        onClick={item.onClick}
                        className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 border-transparent hover:border-primary/40 hover:text-primary/80`}
                      >
                        {renderTitle(item)}
                      </button>
                    ) : item.dropdown ? (
                      <div>
                        <button type="button"
                          onClick={() => toggleDropdown(item.id)}
                          aria-expanded={activeDropdown === item.id}
                          aria-controls={`desktop-dropdown-${item.id}`}
                          className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 ${
                            isNavMatch(pathname ?? "", item.url) ||
                            (item.items && item.items.some((subItem) => isNavMatch(pathname ?? "", subItem.url)))
                              ? "border-primary text-primary"
                              : "border-transparent hover:border-primary/40 hover:text-primary/80"
                          }`}
                        >
                          {renderTitle(item)}
                          <ChevronDown
                            className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === item.id ? "rotate-180" : ""}`}
                          />
                        </button>{" "}
                        {activeDropdown === item.id && item.items && (
                          <div
                            id={`desktop-dropdown-${item.id}`}
                            role="group"
                            aria-label={`${item.title} submenu`}
                            className="absolute left-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-popover border border-border ring-1 ring-border z-50"
                          >
                            {item.items.map((subItem, index, items) => (
                              <React.Fragment key={subItem.id}>
                              {sectionLabel(subItem, items[index - 1])}
                              {subItem.onClick ? (
                                <button type="button"
                                  onClick={() => {
                                    if (subItem.onClick) {
                                      subItem.onClick();
                                      setActiveDropdown(null);
                                    }
                                  }}
                                  className={`block w-full text-left px-4 py-2 text-sm ${
                                    isNavMatch(pathname ?? "", subItem.url)
                                      ? "bg-primary/10 text-primary"
                                      : "text-foreground hover:bg-foreground/5"
                                  }`}
                                >
                                  {renderTitle(subItem)}
                                </button>
                              ) : (
                                <Link
                                  href={subItem.url}
                                  className={`block px-4 py-2 text-sm ${
                                    isNavMatch(pathname ?? "", subItem.url)
                                      ? "bg-primary/10 text-primary"
                                      : "text-foreground hover:bg-foreground/5"
                                  }`}
                                >
                                  {renderTitle(subItem)}
                                </Link>
                              )}
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : item.external ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium border-transparent hover:border-primary/40 hover:text-primary/80`}
                      >
                        {renderTitle(item)}
                      </a>
                    ) : item.title === "Sign Up" || item.title === "Start free trial" ? (
                      <HashAwareLink
                        href={item.url}
                        onClick={() =>
                          trackMarketingCtaClick({
                            surface: "navbar_desktop",
                            cta: "start_trial",
                            destination: item.url,
                          })
                        }
                        className="inline-flex h-11 min-w-[8.5rem] items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm"
                      >
                        {renderTitle(item)}
                      </HashAwareLink>
                    ) : (
                      <Link
                        href={item.url}
                        className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                          isNavMatch(pathname ?? "", item.url)
                            ? "border-primary text-primary"
                            : "border-transparent hover:border-primary/40 hover:text-primary/80"
                        }`}
                      >
                    {renderTitle(item)}
                      </Link>
                    )}
                  </div>
                ))}
          </div>
        </div>
      </div>
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 md:hidden animate-overlay-fade-in motion-reduce:animate-none"
            aria-hidden="true"
            onClick={closeMobileMenu}
          />
          <div
            className="absolute inset-x-0 top-full flex max-h-[calc(100dvh-7rem)] flex-col bg-background border-b border-border shadow-xl md:hidden animate-menu-slide-in motion-reduce:animate-none"
            id="mobile-menu"
          >
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pt-2 pb-3 space-y-1">
              {mobileNavItems.map((item) => (
                <div key={item.id} className="px-4">
                  {item.onClick ? (
                    <button type="button"
                      onClick={() => {
                        if (item.onClick) item.onClick();
                        closeMobileMenu();
                      }}
                      className="flex items-center justify-between w-full min-h-[44px] py-2 pl-3 pr-4 text-base font-medium text-foreground hover:bg-foreground/5 hover:border-l-4 hover:border-primary/40"
                    >
                        {renderTitle(item)}
                    </button>
                  ) : item.dropdown ? (
                    <div
                      ref={(el) => {
                        mobileDropdownRefs.current[item.id] = el;
                      }}
                    >
                      <button type="button"
                        onClick={() => toggleDropdown(item.id)}
                        aria-expanded={activeDropdown === item.id}
                        aria-controls={`mobile-dropdown-${item.id}`}
                        className={`flex items-center justify-between w-full min-h-[44px] py-2 pl-3 pr-4 text-base font-medium ${
                          isNavMatch(pathname ?? "", item.url) || (item.items && item.items.some((subItem) => isNavMatch(pathname ?? "", subItem.url)))
                            ? "bg-primary/10 text-primary border-l-4 border-primary"
                            : "text-foreground hover:bg-foreground/5 hover:border-l-4 hover:border-primary/40"
                        }`}
                      >
                        {renderTitle(item)}
                        <ChevronDown
                          className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === item.id ? "rotate-180" : ""}`}
                        />
                      </button>{" "}
                      {activeDropdown === item.id && item.items && (
                        <div
                          id={`mobile-dropdown-${item.id}`}
                          role="group"
                            aria-label={`${item.title} submenu`}
                          className="mt-2 space-y-1 pl-4"
                        >
                          {item.items.map((subItem, index, items) => (
                            <React.Fragment key={subItem.id}>
                            {sectionLabel(subItem, items[index - 1])}
                            {subItem.onClick ? (
                              <button type="button"
                                onClick={() => {
                                  if (subItem.onClick) {
                                    subItem.onClick();
                                    closeMobileMenu();
                                  }
                                }}
                                className={`flex items-center w-full min-h-[44px] text-left py-2 pl-3 pr-4 text-base font-medium ${
                                  isNavMatch(pathname ?? "", subItem.url)
                                    ? "bg-primary/10 text-primary border-l-4 border-primary"
                                    : "text-foreground hover:bg-foreground/5 hover:border-l-4 hover:border-primary/40"
                                }`}
                              >
                                {renderTitle(subItem)}
                              </button>
                            ) : (
                              <Link
                                href={subItem.url}
                                onClick={closeMobileMenu}
                                className={`flex items-center min-h-[44px] py-2 pl-3 pr-4 text-base font-medium ${
                                  isNavMatch(pathname ?? "", subItem.url)
                                    ? "bg-primary/10 text-primary border-l-4 border-primary"
                                    : "text-foreground hover:bg-foreground/5 hover:border-l-4 hover:border-primary/40"
                                }`}
                              >
                                {renderTitle(subItem)}
                              </Link>
                            )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : item.external ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeMobileMenu}
                      className="flex items-center min-h-[44px] py-2 pl-3 pr-4 text-base font-medium text-foreground hover:bg-foreground/5 hover:border-l-4 hover:border-primary/40"
                    >
                      {renderTitle(item)}
                    </a>
                  ) : (
                    <HashAwareLink
                      href={item.url}
                      onClick={closeMobileMenu}
                      className={`flex items-center min-h-[44px] py-2 pl-3 pr-4 text-base font-medium ${
                        isNavMatch(pathname ?? "", item.url)
                          ? "bg-primary/10 text-primary border-l-4 border-primary"
                          : "text-foreground hover:bg-foreground/5 hover:border-l-4 hover:border-primary/40"
                      }`}
                    >
                      {renderTitle(item)}
                    </HashAwareLink>
                  )}
                </div>
              ))}
            </div>
            {mobileAuthItems.length > 0 && (
              <div className="flex gap-3 border-t border-border px-4 py-3">
                {mobileAuthItems.map((item) => (
                  <HashAwareLink
                    key={item.id}
                    href={item.url}
                    planChooser={item.title === "Start free trial" ? "navbar_mobile_menu" : undefined}
                    onClick={closeMobileMenu}
                    className={`flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-xl text-base font-semibold transition-colors ${
                      item.title === "Sign In"
                        ? "border border-border text-foreground hover:bg-foreground/5"
                        : "bg-primary text-primary-foreground hover:bg-primary/90"
                    }`}
                  >
                    {renderTitle(item)}
                  </HashAwareLink>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </nav>
  );
};

export default NavBar;
